/**
 * Level11Scene - 关卡11：光速限制
 * @description 教学目标：理解光速是宇宙速度上限，v < c
 * 核心公式：γ = 1/√(1 - v²/c²)（洛伦兹因子）
 * 相对论速度叠加：v = (v₁ + v₂) / (1 + v₁v₂/c²)
 */

import Phaser from 'phaser';
import type { LevelConfig } from '@/levels/LevelConfig.ts';
import { TutorialUI } from '@/ui/TutorialUI.ts';
import { getTutorial } from '@/data/tutorials.ts';
import { getGameState } from '@/core/GameState.ts';
import { Relativity, SPEED_OF_LIGHT } from '@/physics/Relativity.ts';
import { type RelativityEffectConfig } from '@/shaders/RelativityShader.ts';

// 关卡11特定配置
interface Level11SpecificConfig {
  /** 光速常量（游戏单位） */
  lightSpeed: number;
  /** 可选速度列表 */
  velocityOptions: number[];
  /** 初始速度 */
  initialVelocity: number;
  /** 目标速度比率 */
  targetVelocityRatio: number;
  /** 目标区域位置 x */
  targetX: number;
  /** 目标区域位置 y */
  targetY: number;
  /** 目标区域半径 */
  targetRadius: number;
  /** 公式 */
  formula: string;
  /** 提示 */
  hint: string;
}

// 游戏状态类型
type GameState = 'idle' | 'running' | 'success' | 'failed';

// 游戏尺寸常量
const GAME_WIDTH = 800;
const GAME_HEIGHT = 600;

/**
 * Level11Scene 类
 * 关卡11场景，实现光速限制教学
 */
export class Level11Scene extends Phaser.Scene {
  // 关卡配置
  private levelConfig: LevelConfig | null = null;
  private levelSpecific: Level11SpecificConfig | null = null;

  // 游戏状态
  private gameState: GameState = 'idle';
  private elapsedTime: number = 0;

  // 相对论计算模块
  private relativity: Relativity;

  // 飞船属性
  private ship: Phaser.GameObjects.Graphics | null = null;
  private shipPosition: { x: number; y: number } = { x: 100, y: 300 };
  private shipVelocity: number = 0;
  private selectedVelocityBoost: number = 0;

  // 目标区域
  private targetZone: Phaser.GameObjects.Graphics | null = null;

  // 光束效果
  private lightBeam: Phaser.GameObjects.Graphics | null = null;

  // 轨迹记录
  private trajectoryPoints: Array<{ x: number; y: number }> = [];
  private trajectoryGraphics: Phaser.GameObjects.Graphics | null = null;

  // UI 元素
  private velocityText: Phaser.GameObjects.Text | null = null;
  private gammaText: Phaser.GameObjects.Text | null = null;
  private ratioText: Phaser.GameObjects.Text | null = null;
  private hintText: Phaser.GameObjects.Text | null = null;
  private resultText: Phaser.GameObjects.Text | null = null;
  private velocityButtons: Phaser.GameObjects.Graphics[] = [];
  private startButton: Phaser.GameObjects.Graphics | null = null;

  // 教程组件
  private tutorialUI: TutorialUI | null = null;

  // 相对论视觉效果
  private shaderTime: number = 0;

  constructor() {
    super({ key: 'Level11Scene' });
    this.relativity = new Relativity(SPEED_OF_LIGHT);
  }

  preload(): void {
    // 加载关卡配置
    this.load.json('level-11', 'src/levels/levels/level-11.json');
  }

  create(): void {
    // 加载关卡配置
    this.levelConfig = this.cache.json.get('level-11') as LevelConfig;

    if (this.levelConfig && this.levelConfig.levelSpecific) {
      this.levelSpecific = this.levelConfig.levelSpecific as unknown as Level11SpecificConfig;
    }

    // 初始化相对论模块的光速值
    if (this.levelSpecific) {
      this.relativity.setLightSpeed(this.levelSpecific.lightSpeed);
    }

    // 创建背景
    this.createBackground();

    // 创建目标区域
    this.createTargetZone();

    // 创建光束效果
    this.createLightBeam();

    // 创建飞船
    this.createShip();

    // 创建轨迹画布
    this.trajectoryGraphics = this.add.graphics();
    this.trajectoryGraphics.setDepth(3);

    // 创建 UI
    this.createUI();

    // 设置输入事件
    this.setupInputEvents();

    // 显示教程
    this.showTutorial();

    // 初始化显示
    this.updateVelocityDisplay();
  }

  override update(_time: number, delta: number): void {
    if (this.gameState !== 'running') return;

    // 更新时间
    this.elapsedTime += delta / 1000;
    this.shaderTime += delta / 1000;

    // 更新飞船运动
    this.updateShipMovement(delta);

    // 更新视觉效果
    this.updateVisualEffects();

    // 检测胜利/失败
    this.checkGameEnd();
  }

  /**
   * 创建背景
   */
  private createBackground(): void {
    // 深空背景
    this.cameras.main.setBackgroundColor('#050510');

    // 添加星空效果
    const starfield = this.add.graphics();
    starfield.setDepth(0);

    // 绘制星星
    for (let i = 0; i < 100; i++) {
      const x = Math.random() * GAME_WIDTH;
      const y = Math.random() * GAME_HEIGHT;
      const size = Math.random() * 2 + 0.5;
      const brightness = Math.random() * 0.5 + 0.3;

      starfield.fillStyle(0xffffff, brightness);
      starfield.fillCircle(x, y, size);
    }

    // 添加网格线作为参考
    const gridGraphics = this.add.graphics();
    gridGraphics.lineStyle(1, 0x1a1a3a, 0.2);

    for (let x = 0; x <= GAME_WIDTH; x += 50) {
      gridGraphics.beginPath();
      gridGraphics.moveTo(x, 0);
      gridGraphics.lineTo(x, GAME_HEIGHT);
      gridGraphics.strokePath();
    }

    for (let y = 0; y <= GAME_HEIGHT; y += 50) {
      gridGraphics.beginPath();
      gridGraphics.moveTo(0, y);
      gridGraphics.lineTo(GAME_WIDTH, y);
      gridGraphics.strokePath();
    }

    // 光速参考线
    const lightLine = this.add.graphics();
    lightLine.lineStyle(3, 0xffff00, 0.5);
    lightLine.beginPath();
    lightLine.moveTo(50, GAME_HEIGHT - 50);
    lightLine.lineTo(GAME_WIDTH - 50, GAME_HEIGHT - 50);
    lightLine.strokePath();

    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 35, '光速 c = 1（宇宙速度上限）', {
      fontSize: '14px',
      color: '#ffff00',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
  }

  /**
   * 创建目标区域
   */
  private createTargetZone(): void {
    if (!this.levelSpecific) return;

    this.targetZone = this.add.graphics();
    this.targetZone.fillStyle(0x4ade80, 0.3);
    this.targetZone.fillCircle(
      this.levelSpecific.targetX,
      GAME_HEIGHT - this.levelSpecific.targetY,
      this.levelSpecific.targetRadius
    );
    this.targetZone.lineStyle(3, 0x4ade80);
    this.targetZone.strokeCircle(
      this.levelSpecific.targetX,
      GAME_HEIGHT - this.levelSpecific.targetY,
      this.levelSpecific.targetRadius
    );

    this.add.text(
      this.levelSpecific.targetX,
      GAME_HEIGHT - this.levelSpecific.targetY + this.levelSpecific.targetRadius + 20,
      '目标区域',
      {
        fontSize: '14px',
        color: '#4ade80',
        fontFamily: 'Arial',
      }
    ).setOrigin(0.5);
  }

  /**
   * 创建光束效果
   */
  private createLightBeam(): void {
    this.lightBeam = this.add.graphics();
    this.lightBeam.setDepth(2);

    // 绘制一条"光"作为参考
    this.time.addEvent({
      delay: 100,
      callback: () => {
        this.lightBeam?.clear();
        const pulseIntensity = Math.sin(this.elapsedTime * 3) * 0.3 + 0.7;

        // 光束线
        this.lightBeam?.lineStyle(4, 0xffff00, pulseIntensity * 0.8);
        this.lightBeam?.beginPath();
        this.lightBeam?.moveTo(50, GAME_HEIGHT - 50);
        this.lightBeam?.lineTo(50 + 50 * pulseIntensity, GAME_HEIGHT - 50);
        this.lightBeam?.strokePath();

        // 光束发光效果
        this.lightBeam?.fillStyle(0xffff00, pulseIntensity * 0.2);
        this.lightBeam?.fillCircle(50, GAME_HEIGHT - 50, 8 * pulseIntensity);
      },
      loop: true,
    });
  }

  /**
   * 创建飞船
   */
  private createShip(): void {
    const playerConfig = this.levelConfig?.player;
    this.shipPosition = {
      x: playerConfig?.startPosition.x ?? 100,
      y: playerConfig?.startPosition.y ?? 300,
    };

    this.ship = this.add.graphics();
    this.ship.setDepth(5);
    this.renderShip();
  }

  /**
   * 渲染飞船
   */
  private renderShip(): void {
    if (!this.ship) return;

    this.ship.clear();

    const screenX = this.shipPosition.x;
    const screenY = GAME_HEIGHT - this.shipPosition.y;

    // 根据速度计算颜色和大小变化（相对论效应）
    const velocityRatio = this.relativity.calculateVelocityRatio(this.shipVelocity);
    const gamma = velocityRatio < 1 ? this.relativity.calculateGamma(this.shipVelocity) : 1;

    // 长度收缩效果（飞船在运动方向上缩短）
    const contractedLength = 30 / gamma;

    // 颜色变化（接近光速时发光）
    const glowIntensity = Math.min(velocityRatio * 0.5, 0.5);
    const baseColor = velocityRatio > 0.8 ? 0xffffff : 0x00aaff;

    // 飞船主体（箭头形状）
    this.ship.fillStyle(baseColor, 1);
    this.ship.beginPath();
    this.ship.moveTo(screenX + contractedLength, screenY);
    this.ship.lineTo(screenX - 10, screenY - 10);
    this.ship.lineTo(screenX - 10, screenY + 10);
    this.ship.closePath();
    this.ship.fillPath();

    // 发光效果
    if (glowIntensity > 0) {
      this.ship.fillStyle(0xffffaa, glowIntensity * 0.3);
      this.ship.fillCircle(screenX, screenY, 20 + glowIntensity * 10);
    }

    // 推进器火焰
    if (this.gameState === 'running' && velocityRatio > 0.1) {
      const flameLength = 10 + velocityRatio * 20;
      this.ship.fillStyle(0xff6600, 0.8);
      this.ship.beginPath();
      this.ship.moveTo(screenX - 10, screenY);
      this.ship.lineTo(screenX - 10 - flameLength, screenY - 5);
      this.ship.lineTo(screenX - 10 - flameLength * 1.5, screenY);
      this.ship.lineTo(screenX - 10 - flameLength, screenY + 5);
      this.ship.closePath();
      this.ship.fillPath();
    }
  }

  /**
   * 创建 UI
   */
  private createUI(): void {
    // 标题
    this.add.text(GAME_WIDTH / 2, 40, '关卡11：光速限制', {
      fontSize: '28px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // 公式显示
    this.add.text(GAME_WIDTH / 2, 75, '洛伦兹因子: γ = 1/√(1 - v²/c²)', {
      fontSize: '18px',
      color: '#ffff00',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 速度叠加公式
    this.add.text(GAME_WIDTH / 2, 100, '速度叠加: v = (v₁ + v₂) / (1 + v₁v₂/c²)', {
      fontSize: '14px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 提示文本
    this.hintText = this.add.text(
      GAME_WIDTH / 2,
      130,
      this.levelSpecific?.hint ?? '选择速度增量，观察相对论效应',
      {
        fontSize: '16px',
        color: '#888888',
        fontFamily: 'Arial',
      }
    ).setOrigin(0.5);

    // 当前速度显示面板
    this.createVelocityPanel();

    // 速度选择按钮
    this.createVelocityButtons();

    // 开始按钮
    this.createStartButton();

    // 结果文本（初始隐藏）
    this.resultText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, '', {
      fontSize: '28px',
      color: '#4ade80',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5).setVisible(false);

    // 物理说明区域
    this.createPhysicsInfo();
  }

  /**
   * 创建速度显示面板
   */
  private createVelocityPanel(): void {
    const panelX = 100;
    const panelY = 200;
    const panelWidth = 200;
    const panelHeight = 120;

    const panel = this.add.graphics();
    panel.fillStyle(0x1a1a3a, 0.8);
    panel.fillRoundedRect(panelX, panelY, panelWidth, panelHeight, 10);
    panel.lineStyle(2, 0x3a3a5a);
    panel.strokeRoundedRect(panelX, panelY, panelWidth, panelHeight, 10);

    // 速度百分比
    this.add.text(panelX + panelWidth / 2, panelY + 20, '当前速度', {
      fontSize: '14px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    this.velocityText = this.add.text(panelX + panelWidth / 2, panelY + 45, '0.00 c', {
      fontSize: '24px',
      color: '#00aaff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // 洛伦兹因子
    this.add.text(panelX + panelWidth / 2, panelY + 70, '洛伦兹因子 γ', {
      fontSize: '12px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    this.gammaText = this.add.text(panelX + panelWidth / 2, panelY + 95, '1.00', {
      fontSize: '18px',
      color: '#ffff00',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // 速度比率进度条
    this.createVelocityBar(panelX + panelWidth + 20, panelY);
  }

  /**
   * 创建速度进度条
   */
  private createVelocityBar(x: number, y: number): void {
    const barWidth = 400;
    const barHeight = 30;

    // 背景
    const barBg = this.add.graphics();
    barBg.fillStyle(0x1a1a3a, 0.8);
    barBg.fillRect(x, y, barWidth, barHeight);
    barBg.lineStyle(2, 0x3a3a5a);
    barBg.strokeRect(x, y, barWidth, barHeight);

    // 光速标记
    const lightSpeedMark = this.add.graphics();
    lightSpeedMark.lineStyle(2, 0xffff00, 1);
    lightSpeedMark.beginPath();
    lightSpeedMark.moveTo(x + barWidth - 10, y);
    lightSpeedMark.lineTo(x + barWidth - 10, y + barHeight);
    lightSpeedMark.strokePath();

    this.add.text(x + barWidth - 10, y - 15, 'c', {
      fontSize: '14px',
      color: '#ffff00',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // 0.5c 标记
    const halfSpeedMark = this.add.graphics();
    halfSpeedMark.lineStyle(1, 0x888888, 0.5);
    halfSpeedMark.beginPath();
    halfSpeedMark.moveTo(x + barWidth / 2 - 5, y);
    halfSpeedMark.lineTo(x + barWidth / 2 - 5, y + barHeight);
    halfSpeedMark.strokePath();

    this.add.text(x + barWidth / 2 - 5, y - 15, '0.5c', {
      fontSize: '12px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 比率文本
    this.ratioText = this.add.text(x + barWidth / 2, y + barHeight + 15, '速度比率: 0%', {
      fontSize: '14px',
      color: '#ffffff',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 进度条填充（将在 updateVelocityDisplay 中更新）
    this.createVelocityBarFill(x, y, barWidth, barHeight);
  }

  /**
   * 创建进度条填充
   */
  private createVelocityBarFill(x: number, y: number, width: number, height: number): void {
    const fillGraphics = this.add.graphics();
    fillGraphics.setDepth(1);
    this.time.addEvent({
      delay: 50,
      callback: () => {
        fillGraphics.clear();
        const ratio = this.relativity.calculateVelocityRatio(this.shipVelocity);
        const fillWidth = Math.min(ratio * width, width - 5);

        // 根据速度比率变化颜色
        let fillColor: number;
        if (ratio < 0.3) {
          fillColor = 0x00aaff; // 蓝色 - 低速
        } else if (ratio < 0.6) {
          fillColor = 0x00ff88; // 绿色 - 中速
        } else if (ratio < 0.9) {
          fillColor = 0xffaa00; // 橙色 - 高速
        } else {
          fillColor = 0xffff00; // 黄色 - 接近光速
        }

        fillGraphics.fillStyle(fillColor, 0.8);
        fillGraphics.fillRect(x + 2, y + 2, fillWidth, height - 4);
      },
      loop: true,
    });
  }

  /**
   * 创建速度选择按钮
   */
  private createVelocityButtons(): void {
    if (!this.levelSpecific) return;

    const velocities = this.levelSpecific.velocityOptions;
    const startX = GAME_WIDTH / 2 - (velocities.length * 80) / 2;
    const y = 520;

    this.add.text(GAME_WIDTH / 2, y - 35, '选择速度增量（相对论叠加）:', {
      fontSize: '14px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    velocities.forEach((velocity, i) => {
      const x = startX + i * 80 + 40;
      const btn = this.add.graphics();
      const isSelected = this.selectedVelocityBoost === velocity;

      btn.fillStyle(isSelected ? 0x00aaff : 0x4a4a6a, isSelected ? 1 : 0.8);
      btn.fillRoundedRect(x - 35, y, 70, 45, 8);
      btn.lineStyle(2, isSelected ? 0x00aaff : 0x6a6a8a);
      btn.strokeRoundedRect(x - 35, y, 70, 45, 8);

      this.add.text(x, y + 15, `${velocity.toFixed(1)}c`, {
        fontSize: '16px',
        color: '#ffffff',
        fontFamily: 'Arial',
        fontStyle: 'bold',
      }).setOrigin(0.5);

      this.add.text(x, y + 35, `+${(velocity * 100).toFixed(0)}%`, {
        fontSize: '10px',
        color: '#888888',
        fontFamily: 'Arial',
      }).setOrigin(0.5);

      btn.setInteractive(
        new Phaser.Geom.Rectangle(x - 35, y, 70, 45),
        Phaser.Geom.Rectangle.Contains
      );

      btn.on('pointerdown', () => {
        if (this.gameState === 'idle') {
          this.selectedVelocityBoost = velocity;
          this.updateVelocityButtons();
          this.updateVelocityPreview();
        }
      });

      btn.on('pointerover', () => {
        if (this.gameState === 'idle') {
          btn.clear();
          btn.fillStyle(0x00aaff, 0.9);
          btn.fillRoundedRect(x - 35, y, 70, 45, 8);
          btn.strokeRoundedRect(x - 35, y, 70, 45, 8);
        }
      });

      btn.on('pointerout', () => {
        if (this.gameState === 'idle') {
          this.updateVelocityButtons();
        }
      });

      this.velocityButtons.push(btn);
    });
  }

  /**
   * 更新速度按钮状态
   */
  private updateVelocityButtons(): void {
    if (!this.levelSpecific) return;

    const velocities = this.levelSpecific.velocityOptions;
    const startX = GAME_WIDTH / 2 - (velocities.length * 80) / 2;
    const y = 520;

    this.velocityButtons.forEach((btn, i) => {
      const velocity = velocities[i];
      if (velocity === undefined) return;
      const x = startX + i * 80 + 40;
      const isSelected = this.selectedVelocityBoost === velocity;

      btn.clear();
      btn.fillStyle(isSelected ? 0x00aaff : 0x4a4a6a, isSelected ? 1 : 0.8);
      btn.fillRoundedRect(x - 35, y, 70, 45, 8);
      btn.lineStyle(2, isSelected ? 0x00aaff : 0x6a6a8a);
      btn.strokeRoundedRect(x - 35, y, 70, 45, 8);
    });
  }

  /**
   * 更新速度预览（显示叠加结果）
   */
  private updateVelocityPreview(): void {
    if (!this.levelSpecific || this.selectedVelocityBoost === 0) return;

    // 计算相对论速度叠加
    const currentVelocity = this.levelSpecific.initialVelocity;
    const boost = this.selectedVelocityBoost;

    try {
      const result = this.relativity.velocityAddition(currentVelocity, boost);

      // 更新提示文本
      const classicResult = currentVelocity + boost; // 经典叠加
      this.hintText?.setText(
        `经典叠加: ${(classicResult).toFixed(2)}c → 相对论叠加: ${result.resultantRatio.toFixed(2)}c`
      );
    } catch (error) {
      this.hintText?.setText('速度叠加计算中...');
    }
  }

  /**
   * 创建物理信息显示
   */
  private createPhysicsInfo(): void {
    this.add.text(50, GAME_HEIGHT - 80, '光速限制: v < c（任何物体不能超过光速）', {
      fontSize: '12px',
      color: '#888888',
      fontFamily: 'Arial',
    });

    this.add.text(50, GAME_HEIGHT - 60, '相对论动能: E = (γ-1)mc²', {
      fontSize: '12px',
      color: '#888888',
      fontFamily: 'Arial',
    });
  }

  /**
   * 创建开始按钮
   */
  private createStartButton(): void {
    this.startButton = this.add.graphics();
    this.startButton.fillStyle(0x4ade80, 1);
    this.startButton.fillRoundedRect(GAME_WIDTH - 150, 520, 130, 50, 10);
    this.startButton.lineStyle(2, 0x4ade80);
    this.startButton.strokeRoundedRect(GAME_WIDTH - 150, 520, 130, 50, 10);

    this.add.text(GAME_WIDTH - 85, 545, '加速!', {
      fontSize: '20px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.startButton.setInteractive(
      new Phaser.Geom.Rectangle(GAME_WIDTH - 150, 520, 130, 50),
      Phaser.Geom.Rectangle.Contains
    );
    this.startButton.on('pointerdown', () => this.startGame());

    this.startButton.on('pointerover', () => {
      this.startButton?.clear();
      this.startButton?.fillStyle(0x5aee90, 1);
      this.startButton?.fillRoundedRect(GAME_WIDTH - 150, 520, 130, 50, 10);
      this.startButton?.strokeRoundedRect(GAME_WIDTH - 150, 520, 130, 50, 10);
    });

    this.startButton.on('pointerout', () => {
      this.startButton?.clear();
      this.startButton?.fillStyle(0x4ade80, 1);
      this.startButton?.fillRoundedRect(GAME_WIDTH - 150, 520, 130, 50, 10);
      this.startButton?.strokeRoundedRect(GAME_WIDTH - 150, 520, 130, 50, 10);
    });
  }

  /**
   * 设置输入事件
   */
  private setupInputEvents(): void {
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (this.gameState !== 'idle') return;

      if (event.key === 'Enter') {
        this.startGame();
      }

      if (event.key === 'r' || event.key === 'R') {
        this.resetGame();
      }
    });
  }

  /**
   * 更新速度显示
   */
  private updateVelocityDisplay(): void {
    const ratio = this.relativity.calculateVelocityRatio(this.shipVelocity);
    let gamma = 1;

    try {
      gamma = this.relativity.calculateGamma(this.shipVelocity);
    } catch {
      // 速度接近光速时
      gamma = Infinity;
    }

    this.velocityText?.setText(`${ratio.toFixed(2)} c`);

    if (gamma === Infinity) {
      this.gammaText?.setText('∞ (接近光速)');
    } else {
      this.gammaText?.setText(gamma.toFixed(2));
    }

    this.ratioText?.setText(`速度比率: ${(ratio * 100).toFixed(1)}%`);
  }

  /**
   * 更新飞船运动
   */
  private updateShipMovement(delta: number): void {
    if (!this.levelSpecific) return;

    const dt = delta / 1000;

    // 简化的运动模型：速度决定位置变化
    // 使用相对论速度作为实际位移速率
    const velocityRatio = this.relativity.calculateVelocityRatio(this.shipVelocity);

    // 位移 = 速度 × 时间（缩放以适应屏幕）
    const displacementScale = 100; // 屏幕像素/单位速度
    const displacement = velocityRatio * displacementScale * dt;

    this.shipPosition.x += displacement;

    // 记录轨迹
    this.trajectoryPoints.push({ x: this.shipPosition.x, y: this.shipPosition.y });
    if (this.trajectoryPoints.length > 50) {
      this.trajectoryPoints.shift();
    }

    // 绘制轨迹
    this.drawTrajectory();

    // 渲染飞船
    this.renderShip();

    // 更新显示
    this.updateVelocityDisplay();
  }

  /**
   * 绘制轨迹
   */
  private drawTrajectory(): void {
    if (!this.trajectoryGraphics || this.trajectoryPoints.length < 2) return;

    this.trajectoryGraphics.clear();

    // 根据速度变化颜色
    const ratio = this.relativity.calculateVelocityRatio(this.shipVelocity);
    let trailColor: number;
    if (ratio < 0.3) {
      trailColor = 0x00aaff;
    } else if (ratio < 0.6) {
      trailColor = 0x00ff88;
    } else if (ratio < 0.9) {
      trailColor = 0xffaa00;
    } else {
      trailColor = 0xffff00;
    }

    this.trajectoryGraphics.lineStyle(3, trailColor, 0.5);
    this.trajectoryGraphics.beginPath();

    const firstPoint = this.trajectoryPoints[0];
    if (firstPoint) {
      this.trajectoryGraphics.moveTo(firstPoint.x, GAME_HEIGHT - firstPoint.y);
    }

    for (let i = 1; i < this.trajectoryPoints.length; i++) {
      const point = this.trajectoryPoints[i];
      if (point) {
        this.trajectoryGraphics.lineTo(point.x, GAME_HEIGHT - point.y);
      }
    }

    this.trajectoryGraphics.strokePath();
  }

  /**
   * 更新视觉效果
   */
  private updateVisualEffects(): void {
    // 相对论视觉效果（如果速度足够高）
    const ratio = this.relativity.calculateVelocityRatio(this.shipVelocity);

    if (ratio > 0.5 && this.ship) {
      try {
        // 应用相对论着色器效果
        const effectConfig: RelativityEffectConfig = {
          velocity: ratio,
          direction: { x: 1, y: 0 },
          time: this.shaderTime,
          center: { x: this.shipPosition.x, y: GAME_HEIGHT - this.shipPosition.y },
        };

        // 注意：由于着色器需要 WebGL Pipeline 支持，这里使用简化效果
        // 实际项目中应该完整实现 applyRelativityEffect
        this.applySimplifiedRelativityEffect(effectConfig);
      } catch (error) {
        // 着色器效果失败时忽略
      }
    }
  }

  /**
   * 简化的相对论视觉效果（不使用 WebGL Pipeline）
   */
  private applySimplifiedRelativityEffect(config: RelativityEffectConfig): void {
    // 添加速度发光效果
    if (this.ship && config.velocity > 0.8) {
      const glowGraphics = this.add.graphics();
      glowGraphics.setDepth(4);
      const pulse = Math.sin(this.shaderTime * 10) * 0.3 + 0.7;
      glowGraphics.fillStyle(0xffffaa, pulse * 0.3 * config.velocity);
      glowGraphics.fillCircle(
        this.shipPosition.x,
        GAME_HEIGHT - this.shipPosition.y,
        30 + config.velocity * 20
      );

      // 短暂显示后消失
      this.time.delayedCall(100, () => {
        glowGraphics.destroy();
      });
    }
  }

  /**
   * 开始游戏
   */
  private startGame(): void {
    if (this.gameState !== 'idle') return;
    if (this.selectedVelocityBoost === 0) {
      this.hintText?.setText('请先选择速度增量！');
      return;
    }

    if (!this.levelSpecific) return;

    this.gameState = 'running';
    this.elapsedTime = 0;

    // 使用相对论速度叠加
    try {
      const result = this.relativity.velocityAddition(
        this.levelSpecific.initialVelocity,
        this.selectedVelocityBoost
      );
      this.shipVelocity = result.resultantVelocity;
    } catch {
      this.shipVelocity = this.levelSpecific.initialVelocity + this.selectedVelocityBoost;
      // 限制不超过光速
      this.shipVelocity = Math.min(this.shipVelocity, SPEED_OF_LIGHT * 0.99);
    }

    // 清除轨迹
    this.trajectoryPoints = [];
    if (this.trajectoryGraphics) {
      this.trajectoryGraphics.clear();
    }

    // 隐藏结果
    this.resultText?.setVisible(false);

    // 更新开始按钮状态
    this.updateStartButtonState('running');

    // 更新提示
    this.hintText?.setText('飞船正在接近光速...观察相对论效应！');
  }

  /**
   * 更新开始按钮状态
   */
  private updateStartButtonState(state: GameState): void {
    if (!this.startButton) return;

    this.startButton.clear();

    if (state === 'running') {
      this.startButton.fillStyle(0x6a6a8a, 1);
    } else {
      this.startButton.fillStyle(0x4ade80, 1);
    }
    this.startButton.fillRoundedRect(GAME_WIDTH - 150, 520, 130, 50, 10);
    this.startButton.strokeRoundedRect(GAME_WIDTH - 150, 520, 130, 50, 10);
  }

  /**
   * 检测游戏结束
   */
  private checkGameEnd(): void {
    if (!this.levelSpecific) return;

    // 检测是否到达目标
    const dx = this.shipPosition.x - this.levelSpecific.targetX;
    const dy = this.shipPosition.y - this.levelSpecific.targetY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance <= this.levelSpecific.targetRadius) {
      // 检查速度比率是否达标
      const currentRatio = this.relativity.calculateVelocityRatio(this.shipVelocity);
      if (currentRatio >= this.levelSpecific.targetVelocityRatio) {
        this.gameState = 'success';
        this.showSuccessResult();
        this.updateStartButtonState('idle');
      }
    }

    // 检测是否超出边界
    if (
      this.shipPosition.x < 0 ||
      this.shipPosition.x > GAME_WIDTH ||
      this.shipPosition.y < 0 ||
      this.shipPosition.y > GAME_HEIGHT
    ) {
      this.gameState = 'failed';
      this.showFailResult('飞船超出边界！');
      this.updateStartButtonState('idle');
    }

    // 检测是否到达光速（但无法超过）
    const ratio = this.relativity.calculateVelocityRatio(this.shipVelocity);
    if (ratio >= 0.99) {
      // 达到接近光速时显示特殊信息
      this.hintText?.setText('已接近光速极限！任何物体都无法真正达到光速 c');
    }
  }

  /**
   * 显示成功结果
   */
  private showSuccessResult(): void {
    const gameState = getGameState();
    gameState.completeLevel(11, this.elapsedTime);

    const ratio = this.relativity.calculateVelocityRatio(this.shipVelocity);
    const gamma = this.relativity.calculateGamma(this.shipVelocity);

    this.resultText?.setText(`🎉 成功！达到 ${(ratio * 100).toFixed(1)}% 光速`)
      .setColor('#4ade80')
      .setVisible(true);

    this.hintText?.setText(
      `洛伦兹因子 γ=${gamma.toFixed(2)}，相对论效应显著！时间膨胀，长度收缩！`
    );

    this.createCompletionButtons();
  }

  /**
   * 创建完成后的按钮
   */
  private createCompletionButtons(): void {
    const y = GAME_HEIGHT / 2 + 60;

    const nextBtn = this.add.text(GAME_WIDTH / 2 - 80, y, '下一关', {
      fontSize: '18px',
      color: '#4ade80',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 15, y: 8 },
    }).setOrigin(0.5).setInteractive();

    nextBtn.on('pointerdown', () => {
      const gameState = getGameState();
      if (gameState.isLevelUnlocked(12)) {
        this.scene.start('Level12Scene');
      } else {
        this.scene.start('LevelSelectScene');
      }
    });

    const backBtn = this.add.text(GAME_WIDTH / 2 + 80, y, '返回', {
      fontSize: '18px',
      color: '#888888',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 15, y: 8 },
    }).setOrigin(0.5).setInteractive();

    backBtn.on('pointerdown', () => {
      this.scene.start('LevelSelectScene');
    });
  }

  /**
   * 显示失败结果
   */
  private showFailResult(reason: string): void {
    this.resultText?.setText(`❌ ${reason}`)
      .setColor('#ff6b6b')
      .setVisible(true);

    this.hintText?.setText('提示：选择合适的速度增量，利用相对论叠加到达目标速度');
  }

  /**
   * 重置游戏
   */
  private resetGame(): void {
    this.gameState = 'idle';
    this.elapsedTime = 0;
    this.shipVelocity = 0;
    this.selectedVelocityBoost = 0;

    // 重置位置
    if (this.levelConfig?.player) {
      this.shipPosition = {
        x: this.levelConfig.player.startPosition.x,
        y: this.levelConfig.player.startPosition.y,
      };
    } else {
      this.shipPosition = { x: 100, y: 300 };
    }

    // 清除轨迹
    this.trajectoryPoints = [];
    if (this.trajectoryGraphics) {
      this.trajectoryGraphics.clear();
    }

    // 渲染飞船
    this.renderShip();

    // 隐藏结果
    this.resultText?.setVisible(false);

    // 重置提示
    this.hintText?.setText(this.levelSpecific?.hint ?? '选择速度增量，观察相对论效应');

    // 重置按钮
    this.updateStartButtonState('idle');
    this.updateVelocityButtons();
    this.updateVelocityDisplay();
  }

  /**
   * 显示教程
   */
  private showTutorial(): void {
    const tutorialData = getTutorial('level-11');
    if (!tutorialData) return;

    this.tutorialUI = new TutorialUI(this, {
      levelNumber: 11,
      tutorialData,
      onStart: () => {
        this.tutorialUI?.destroy();
        this.tutorialUI = null;
      },
      onSkip: () => {
        this.tutorialUI?.destroy();
        this.tutorialUI = null;
      },
    });
  }

  /**
   * 获取当前游戏状态
   */
  getGameState(): GameState {
    return this.gameState;
  }

  /**
   * 获取当前时间
   */
  getElapsedTime(): number {
    return this.elapsedTime;
  }

  /**
   * 获取飞船位置
   */
  getShipPosition(): { x: number; y: number } {
    return this.shipPosition;
  }

  /**
   * 获取飞船速度
   */
  getShipVelocity(): number {
    return this.shipVelocity;
  }

  /**
   * 获取相对论计算模块
   */
  getRelativity(): Relativity {
    return this.relativity;
  }
}